import { useSyncStatus } from "@/grid/hooks/useSyncStatus";
import { SYNC_SCHEDULE_TIMEZONE } from "@/grid/data/sync-job-catalog";
import { cn } from "@/lib/cn";
import { formatDistanceToNow } from "date-fns";
import { ChevronDown, Clock, RefreshCw } from "lucide-react";
import { useState } from "react";
import type { SyncJobLastRun } from "@/grid/data/fetch-sync-status";

function statusColor(status: string | null | undefined): string {
  if (status === "success") return "bg-[var(--color-status-green)]";
  if (status === "partial") return "bg-amber-400";
  if (status === "running") return "bg-sky-400 animate-pulse";
  if (status === "failed") return "bg-[var(--color-destructive)]";
  return "bg-neutral-300";
}

function RegionLine({ label, lastRun }: { label: string; lastRun: SyncJobLastRun | null }) {
  return (
    <div className="flex items-start gap-2 pl-0.5">
      <span
        className={cn("mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full", statusColor(lastRun?.status))}
      />
      <div className="min-w-0 flex-1">
        <span className="text-[10px] font-medium text-[var(--color-foreground)]">{label}</span>
        {lastRun?.finished_at ? (
          <p className="text-[10px] text-[var(--color-muted-foreground)]">
            {lastRun.status} ·{" "}
            {formatDistanceToNow(new Date(lastRun.finished_at), { addSuffix: true })}
            {lastRun.rows_written != null ? ` · ${lastRun.rows_written} rows` : null}
          </p>
        ) : (
          <p className="text-[10px] text-[var(--color-muted-foreground)]">No runs yet</p>
        )}
        {lastRun?.error_detail ? (
          <p className="line-clamp-1 text-[10px] text-amber-700">{lastRun.error_detail}</p>
        ) : null}
      </div>
    </div>
  );
}

interface SyncStatusPanelProps {
  /** Always show sync details (for mobile settings sheet). */
  expanded?: boolean;
}

export function SyncStatusPanel({ expanded = false }: SyncStatusPanelProps) {
  const { data, isLoading, isError, refetch, isFetching } = useSyncStatus();
  const [open, setOpen] = useState(expanded);

  const panel = (
    <>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold text-[var(--color-foreground)]">Warehouse sync</p>
          <p className="text-[10px] text-[var(--color-muted-foreground)]">
            {SYNC_SCHEDULE_TIMEZONE}
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[var(--color-muted-foreground)] hover:bg-neutral-100 disabled:opacity-50"
          aria-label="Refresh sync status"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} />
        </button>
      </div>

      {isLoading ? (
        <p className="text-xs text-[var(--color-muted-foreground)]">Loading…</p>
      ) : isError ? (
        <p className="text-xs text-[var(--color-destructive)]">Could not load sync status</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {data?.map((group) => (
            <li
              key={group.id}
              className="rounded-lg border border-neutral-100 bg-neutral-50/80 px-2.5 py-2"
            >
              <div className="flex items-start gap-2">
                <span
                  className={cn(
                    "mt-1 h-2 w-2 shrink-0 rounded-full",
                    statusColor(group.aggregateStatus),
                  )}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-[var(--color-foreground)]">
                    {group.title}
                  </p>
                  <p className="text-[10px] text-[var(--color-muted-foreground)]">
                    {group.description}
                  </p>
                  <div className="mt-1.5 flex flex-col gap-1 border-l border-neutral-200/80 pl-2">
                    {group.regions.map((region) => (
                      <RegionLine key={region.kind} label={region.label} lastRun={region.lastRun} />
                    ))}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );

  if (expanded) {
    return <div className="w-full">{panel}</div>;
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs text-[var(--color-muted-foreground)] transition-colors hover:bg-neutral-100 hover:text-[var(--color-foreground)]"
        aria-expanded={open}
      >
        <Clock className="h-3.5 w-3.5" />
        Sync
        <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180")} />
      </button>

      {open ? (
        <div className="absolute bottom-full left-0 z-50 mb-2 w-[min(17rem,calc(100vw-2rem))] rounded-xl border border-neutral-200/80 bg-white p-3 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
          {panel}
        </div>
      ) : null}
    </div>
  );
}
